import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { UserGroup } from '@gmn-portal/backend';
import { AlertCircle, Plus } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import { MetadataFieldCard } from './metadata-field-card';
import { MetadataFieldDialog } from './metadata-field-dialog';
import type { FieldError, MetadataField, MetadataGroup } from './types';
import { createNewField, updateOrderValues } from './utils';

interface MetadataFieldListProps {
  fields: MetadataField[];
  groups: MetadataGroup[];
  selectedGroup: MetadataGroup | null;
  onSelectGroup: (id: string) => void;
  disabled?: boolean;
  userGroups?: UserGroup[];
  defaultUpdateGroupKeys?: string[];
  onFieldsChange: (fields: MetadataField[]) => void;
  /** Field-level errors from backend validation */
  fieldErrors?: FieldError[];
  headerRight?: React.ReactNode;
}

export function MetadataFieldList({
  fields,
  groups,
  selectedGroup,
  onSelectGroup,
  disabled = false,
  userGroups,
  defaultUpdateGroupKeys,
  onFieldsChange,
  fieldErrors = [],
  headerRight,
}: MetadataFieldListProps) {
  const { t } = useIntl();
  const [editingField, setEditingField] = useState<MetadataField | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<MetadataField | null>(null);

  const groupFields = fields
    .filter((f) => f.groupId === selectedGroup?.id)
    .sort((a, b) => a.order - b.order);

  // Helper to get error for a specific field
  const getFieldError = (field: MetadataField): FieldError | undefined => {
    return fieldErrors.find((e) => e.fieldKey === field.key || e.fieldKey === field.id);
  };

  // Count errors per group for tab indicator
  const errorsPerGroup = (groupId: string): number => {
    return fields.filter((f) => f.groupId === groupId).filter((f) => getFieldError(f)).length;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = groupFields.findIndex((f) => f.id === active.id);
      const newIndex = groupFields.findIndex((f) => f.id === over.id);

      const reorderedGroupFields = arrayMove(groupFields, oldIndex, newIndex);
      const updatedGroupFields = updateOrderValues(reorderedGroupFields);

      // Merge back with fields from other groups
      const otherFields = fields.filter((f) => f.groupId !== selectedGroup?.id);
      onFieldsChange([...otherFields, ...updatedGroupFields]);
    }
  };

  const handleAddField = () => {
    if (!selectedGroup) return;
    const newField = createNewField(selectedGroup.id, fields);
    setEditingField(newField);
    setIsDialogOpen(true);
  };

  const handleEditField = (field: MetadataField) => {
    setEditingField(field);
    setIsDialogOpen(true);
  };

  const handleSaveField = (updates: Partial<MetadataField>) => {
    if (!editingField) return;

    const targetGroupId = updates.groupId ?? editingField.groupId;
    let updatedField = { ...editingField, ...updates, groupId: targetGroupId };
    if (targetGroupId !== editingField.groupId) {
      const maxOrder = Math.max(
        0,
        ...fields
          .filter((f) => f.groupId === targetGroupId && f.id !== editingField.id)
          .map((f) => f.order)
      );
      updatedField = { ...updatedField, order: maxOrder + 1 };
    }

    // Check if it's a new field (temp id)
    if (editingField.id.startsWith('temp-')) {
      onFieldsChange([...fields, updatedField]);
    } else {
      onFieldsChange(fields.map((f) => (f.id === editingField.id ? updatedField : f)));
    }

    setEditingField(null);
  };

  const handleDeleteField = () => {
    if (!fieldToDelete) return;
    onFieldsChange(fields.filter((f) => f.id !== fieldToDelete.id));
    setFieldToDelete(null);
  };

  if (!selectedGroup) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        {t('metadata.builder.selectGroupFirst')}
      </div>
    );
  }

  // Sort groups by order
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);

  return (
    <>
      <div className="flex-1 flex flex-col min-w-0 bg-card">
        {/* Group Tabs Header */}
        <div
          className="h-11 px-5 border-b flex items-end justify-between gap-3 shrink-0"
          data-testid="metadata-group-tabs-header"
        >
          <div className="flex items-center gap-1 overflow-x-auto -mb-px min-w-0">
            {sortedGroups.map((group) => {
              const errorCount = errorsPerGroup(group.id);
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => onSelectGroup(group.id)}
                  className={cn(
                    'shrink-0 px-3 pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
                    selectedGroup?.id === group.id
                      ? errorCount > 0
                        ? 'border-destructive text-destructive'
                        : 'border-primary text-primary'
                      : errorCount > 0
                        ? 'border-transparent text-destructive hover:border-destructive'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  {group.labelDe}
                  {errorCount > 0 && <AlertCircle className="size-3.5" />}
                </button>
              );
            })}
          </div>

          {headerRight && <div className="shrink-0 pb-1">{headerRight}</div>}
        </div>

        {/* Fields List */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={groupFields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {groupFields.map((field) => (
                  <MetadataFieldCard
                    key={field.id}
                    field={field}
                    userGroups={userGroups}
                    disabled={disabled}
                    onEdit={() => handleEditField(field)}
                    onDelete={() => setFieldToDelete(field)}
                    error={getFieldError(field)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {groupFields.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border-2 border-dashed border-muted rounded-lg bg-muted/30">
                <p className="text-sm">{t('metadata.builder.noFieldsInGroup')}</p>
              </div>
            )}

            {/* Add Button */}
            {!disabled && (
              <div className="pt-2">
                <Button
                  onClick={handleAddField}
                  variant="outline"
                  className="w-full border-2 border-dashed border-muted hover:border-primary/50 hover:bg-primary/5 text-muted-foreground hover:text-primary py-6"
                >
                  <Plus className="size-5 mr-2" />
                  {t('metadata.builder.addFieldOrSection')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Field Edit/Create Dialog */}
      <MetadataFieldDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        field={editingField}
        fields={fields}
        groups={groups}
        onSave={handleSaveField}
        userGroups={userGroups}
        defaultUpdateGroupKeys={defaultUpdateGroupKeys}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!fieldToDelete} onOpenChange={() => setFieldToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('metadata.builder.deleteField')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('metadata.builder.deleteFieldConfirmation', {
                name: fieldToDelete?.labelDe || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteField}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
